/**
 * Kontoauszug lesen: ISO 20022 CAMT.053 (Tagesauszug), CAMT.052 (Umsatzbericht), CAMT.054 (Soll-/Haben-Avis) und als
 * Rückfall CSV mit Spaltenzuordnung. Rein und ohne Abhängigkeiten: ein kleiner XML-Leser, ein CSV-Leser, SHA-256 für
 * Fingerabdrücke und IBAN-Hilfen. Die Oberfläche liest die Datei, die Datenbank bekommt nur, was sie braucht
 * (maskierte IBAN, Prüfwert, Verwendungszweck); die volle fremde IBAN verlässt den Browser nie.
 */
import { isValidIban } from "./document-model.js";
import { foldText } from "./countries.js";
import { isIsoDate } from "./periods.js";
import type { Cents, IsoDate } from "./types.js";

export type StatementFormat = "camt053" | "camt052" | "camt054" | "csv";

export interface BankTransaction {
  /** Referenz der Bank (AcctSvcrRef) bzw. Buchungs-ID aus der CSV; mit „#n“ je Einzelposten einer Sammelbuchung */
  bankRef: string | null;
  endToEndId: string | null;
  bookingDate: IsoDate;
  valueDate: IsoDate | null;
  /** Haben (Eingang) oder Soll (Abgang) aus Sicht des Kontos der Agentur */
  direction: "credit" | "debit";
  amountCents: Cents;
  currency: string;
  /** Auftraggeber bei Eingängen, Empfänger bei Abgängen */
  counterpartyName: string | null;
  counterpartyIban: string | null;
  /** Verwendungszweck (unstrukturiert, zusammengefügt) */
  remittance: string;
  /** Strukturierte Gläubigerreferenz (RF-Referenz nach ISO 11649, QR-Referenz CH oder eigene) */
  creditorRef: string | null;
  /** Genannte Belegnummern (Strd/RfrdDocInf/Nb) */
  referredDocs: string[];
  /** Storno oder Rückbuchung (RvslInd, Rückgabegrund) */
  reversal: boolean;
  returnReason: string | null;
  /** Geschäftsvorfallcode (Domäne/Familie/Unterfamilie oder bankeigen) */
  bankCode: string | null;
  /** Vorgemerkt, noch nicht gebucht (Status PDNG/INFO) */
  pending: boolean;
}

export interface BankStatement {
  format: StatementFormat;
  accountIban: string | null;
  accountCurrency: string | null;
  statementId: string | null;
  from: IsoDate | null;
  to: IsoDate | null;
  closingBalance: { amountCents: Cents; currency: string; date: IsoDate | null } | null;
  transactions: BankTransaction[];
  /** Hinweise zu übersprungenen oder unklaren Buchungen (Klartext) */
  warnings: string[];
}

/** Fachlicher Lesefehler mit Klartext für die Oberfläche. */
export class StatementError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StatementError";
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// SHA-256 (für Fingerabdrücke; im Browser und in Node gleich)
// ═════════════════════════════════════════════════════════════════════════════

function utf8(input: string): number[] {
  const out: number[] = [];
  for (const ch of input) {
    let c = ch.codePointAt(0)!;
    if (c >= 0xd800 && c <= 0xdfff) c = 0xfffd;
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return out;
}

const K256 = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

/** SHA-256 eines Textes (UTF-8) als Hex-Zeichenkette, gleichlautend mit app.sha256_hex der Datenbank. */
export function sha256Hex(input: string): string {
  const bytes = utf8(input);
  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const hi = Math.floor(bitLen / 0x100000000);
  const lo = bitLen >>> 0;
  bytes.push((hi >>> 24) & 255, (hi >>> 16) & 255, (hi >>> 8) & 255, hi & 255, (lo >>> 24) & 255, (lo >>> 16) & 255, (lo >>> 8) & 255, lo & 255);
  const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const w = new Array<number>(64);
  for (let off = 0; off < bytes.length; off += 64) {
    for (let i = 0; i < 16; i++) {
      w[i] = ((bytes[off + i * 4]! << 24) | (bytes[off + i * 4 + 1]! << 16) | (bytes[off + i * 4 + 2]! << 8) | bytes[off + i * 4 + 3]!) >>> 0;
    }
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15]!, b = w[i - 2]!;
      const s0 = ((a >>> 7) | (a << 25)) ^ ((a >>> 18) | (a << 14)) ^ (a >>> 3);
      const s1 = ((b >>> 17) | (b << 15)) ^ ((b >>> 19) | (b << 13)) ^ (b >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h as [number, number, number, number, number, number, number, number];
    for (let i = 0; i < 64; i++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K256[i]! + w[i]!) >>> 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0]! + a) >>> 0; h[1] = (h[1]! + b) >>> 0; h[2] = (h[2]! + c) >>> 0; h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0; h[5] = (h[5]! + f) >>> 0; h[6] = (h[6]! + g) >>> 0; h[7] = (h[7]! + hh) >>> 0;
  }
  return h.map((x) => x.toString(16).padStart(8, "0")).join("");
}

// ═════════════════════════════════════════════════════════════════════════════
// IBAN: normalisieren, maskieren, Prüfwert je Agentur
// ═════════════════════════════════════════════════════════════════════════════

/** IBAN ohne Leerzeichen in Großbuchstaben; null, wenn es keine IBAN ist (auch bei falscher Prüfziffer). */
export function normalizeIban(input: string | null | undefined): string | null {
  if (!input) return null;
  const s = input.replace(/[\s-]+/g, "").toUpperCase();
  return isValidIban(s) ? s : null;
}

/**
 * Maskierte Anzeige einer Kontonummer: Land, Prüfziffern und die letzten vier Stellen („DE89 … 3000“). Andere
 * Kontonummern nur mit den letzten vier Stellen („… 3000“). Die Datenbank nimmt in maskierten Feldern nur genau diese
 * beiden Formen an und speichert so nie eine vollständige fremde IBAN.
 */
export function maskIban(input: string | null | undefined): string | null {
  if (!input) return null;
  const s = input.replace(/[^0-9A-Za-z]+/g, "").toUpperCase();
  if (s.length < 5) return null;
  if (/^[A-Z]{2}\d{2}[A-Z0-9]{6,}$/.test(s)) return `${s.slice(0, 4)} … ${s.slice(-4)}`;
  return `… ${s.slice(-4)}`;
}

/** Genau die Form, die maskIban erzeugt (Gegenprobe der Datenbank für maskierte Felder). */
export const MASKED_ACCOUNT_RE = /^([A-Z]{2}[0-9]{2} )?… [A-Z0-9]{4}$/;

/** Sieht der Text wie eine vollständige IBAN aus? (Gegenprobe der Datenbank: maskierte Felder dürfen das nicht.) */
export function looksLikeFullIban(input: string | null | undefined): boolean {
  return !!input && /^[A-Z]{2}\d{2}[A-Z0-9]{8,30}$/.test(input.replace(/\s+/g, "").toUpperCase());
}

/** Erfundene, aber gültige IBAN aus Land und Kontoteil (Prüfziffer berechnet), z. B. für Beispieldateien. */
export function makeIban(country: string, bban: string): string {
  const b = bban.replace(/\s+/g, "").toUpperCase();
  const rearranged = `${b}${country.toUpperCase()}00`;
  let rem = 0;
  for (const ch of rearranged) {
    const v = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of v) rem = (rem * 10 + Number(d)) % 97;
  }
  return `${country.toUpperCase()}${String(98 - rem).padStart(2, "0")}${b}`;
}

// ═════════════════════════════════════════════════════════════════════════════
// Strukturierte Referenzen: RF (ISO 11649) und QR-Referenz (CH)
// ═════════════════════════════════════════════════════════════════════════════

function mod97(s: string): number {
  let rem = 0;
  for (const ch of s) {
    const v = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of v) rem = (rem * 10 + Number(d)) % 97;
  }
  return rem;
}

/** RF-Gläubigerreferenz (ISO 11649) aus einem Text, z. B. der Rechnungsnummer: „RF“ + Prüfziffern + Buchstaben/Ziffern. */
export function rfReference(payload: string): string | null {
  const p = payload.toUpperCase().replace(/[^0-9A-Z]/g, "");
  if (!p || p.length > 21) return null;
  const check = 98 - mod97(`${p}RF00`);
  return `RF${String(check).padStart(2, "0")}${p}`;
}

/** Inhalt einer gültigen RF-Referenz (ohne „RF“ und Prüfziffern), sonst null. */
export function rfPayload(ref: string | null | undefined): string | null {
  if (!ref) return null;
  const s = ref.replace(/\s+/g, "").toUpperCase();
  if (!/^RF\d{2}[0-9A-Z]{1,21}$/.test(s)) return null;
  return mod97(s.slice(4) + s.slice(0, 4)) === 1 ? s.slice(4) : null;
}

/**
 * Erste gültige RF-Referenz in einem Text („RF18 5390 0754 7034“ → „RF18539007547034“), sonst null. Eine Referenz endet
 * an einer Grenze: nach einer kürzeren Gruppe als vier Zeichen, vor einem Wort, an Satzzeichen oder am Ende
 * („RF45 RE20 2600 08 Lunava“ → „RF45RE20260008“). Geprüft wird zuerst diese natürliche Grenze, danach die übrigen
 * Gruppengrenzen von lang nach kurz; nie mitten in einem Wort (jede Länge bestünde die Prüfziffer mit 1/97).
 */
export function findRfReference(text: string | null | undefined): string | null {
  if (!text) return null;
  const up = text.toUpperCase();
  for (const m of up.matchAll(/\bRF\d{2}/g)) {
    const words = up.slice(m.index).split(/\s+/).filter(Boolean);
    const bounds: string[] = [];
    let acc = "";
    let natural: string | null = null;
    let beforeWord: string | null = null;
    for (let i = 0; i < words.length; i++) {
      const w = words[i]!;
      const token = w.replace(/[.,;:!?)\]]+$/, "");
      if (!/^[0-9A-Z]+$/.test(token) || (i > 0 && token.length > 4) || acc.length + token.length > 25) {
        natural = acc;
        break;
      }
      // kurzes Wort nach der Referenz („… 0061 DANK“, „… MAI 2026“): vermutlich schon Text, zuerst davor prüfen
      if (i > 0 && beforeWord === null && /^[A-Z]{3,}$/.test(token)) beforeWord = acc;
      acc += token;
      if (acc.length >= 5) bounds.push(acc);
      // Ende: kürzere Gruppe, Satzzeichen danach, oder der erste Block ist schon die ganze Referenz („RF18539007547034“)
      if (token !== w || (i > 0 && token.length < 4) || (i === 0 && token.length > 4)) {
        natural = acc;
        break;
      }
    }
    natural = beforeWord ?? natural ?? acc;
    const order = [natural, ...bounds.filter((c) => c !== natural).reverse()];
    for (const cand of order) if (cand.length >= 5 && rfPayload(cand)) return cand;
  }
  return null;
}

/** Inhalt einer gültigen QR-Referenz (27 Ziffern, Prüfziffer Modulo 10 rekursiv) ohne führende Nullen und Prüfziffer. */
export function qrReferencePayload(ref: string | null | undefined): string | null {
  if (!ref) return null;
  const s = ref.replace(/\s+/g, "");
  if (!/^\d{27}$/.test(s)) return null;
  const table = [0, 9, 4, 6, 8, 2, 7, 1, 3, 5];
  let carry = 0;
  for (const d of s.slice(0, 26)) carry = table[(carry + Number(d)) % 10]!;
  if ((10 - carry) % 10 !== Number(s[26])) return null;
  return s.slice(0, 26).replace(/^0+/, "") || null;
}

// ═════════════════════════════════════════════════════════════════════════════
// Beträge und Datumsangaben
// ═════════════════════════════════════════════════════════════════════════════

/** Betrag aus XML („1234.5“, Punkt als Dezimaltrenner) in Cent; mehr als zwei Nachkommastellen nur als Nullen. */
export function parseDecimalCents(input: string, decimal: "." | "," = "."): Cents | null {
  let s = input.trim().replace(/[\s  '’]/g, "").replace(/[€$£]|CHF|EUR|USD|GBP/gi, "");
  if (!s) return null;
  let neg = false;
  if (s.startsWith("-")) { neg = true; s = s.slice(1); }
  else if (s.endsWith("-")) { neg = true; s = s.slice(0, -1); }
  else if (s.startsWith("+")) s = s.slice(1);
  if (s.startsWith("(") && s.endsWith(")")) { neg = true; s = s.slice(1, -1); }
  const thousands = decimal === "," ? "." : ",";
  // Praxistest K-141: „1234.56“ wurde bei Dezimalkomma als 123.456,00 gelesen (der Punkt galt blind als Tausendertrenner).
  // Ein Tausendertrenner steht nur vor dem Dezimaltrenner und gliedert in Gruppen von genau drei Ziffern; alles andere ist
  // eine Zahl im anderen Format und wird abgelehnt statt falsch gelesen.
  const [vorDezimal = "", ...nachDezimal] = s.split(decimal);
  if (nachDezimal.some((x) => x.includes(thousands))) return null;
  if (vorDezimal.includes(thousands) && !new RegExp(`^\\d{1,3}(\\${thousands}\\d{3})+$`).test(vorDezimal)) return null;
  s = s.split(thousands).join("");
  const parts = s.split(decimal);
  if (parts.length > 2) return null;
  const [intPart = "", frac = ""] = parts;
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(frac) || (intPart === "" && frac === "")) return null;
  if (frac.length > 2 && !/^0*$/.test(frac.slice(2))) return null;
  const cents = Number(intPart || "0") * 100 + Number((frac.slice(0, 2) || "0").padEnd(2, "0"));
  if (!Number.isSafeInteger(cents)) return null;
  return neg ? -cents : cents;
}

/** Datum aus Kontoauszügen: 2026-09-25, 25.09.2026, 25.09.26, 25/09/2026, 2026-09-25T10:00:00. */
export function parseStatementDate(input: string | null | undefined): IsoDate | null {
  if (!input) return null;
  const s = input.trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const d = `${m[1]}-${m[2]}-${m[3]}`;
    return isIsoDate(d) ? d : null;
  }
  // Tag.Monat.Jahr mit Punkt, Schrägstrich oder Bindestrich; eine Uhrzeit dahinter (Direktbanken) wird ignoriert
  m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4}|\d{2})(?:[ T,]+\d{1,2}:\d{2}.*)?$/.exec(s);
  if (m) {
    const y = m[3]!.length === 2 ? `20${m[3]}` : m[3]!;
    const d = `${y}-${m[2]!.padStart(2, "0")}-${m[1]!.padStart(2, "0")}`;
    return isIsoDate(d) ? d : null;
  }
  m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (m) {
    const d = `${m[1]}-${m[2]}-${m[3]}`;
    return isIsoDate(d) ? d : null;
  }
  return null;
}

const oneLine = (s: string | null | undefined): string => (s ?? "").replace(/\s+/g, " ").trim();

// ═════════════════════════════════════════════════════════════════════════════
// Kleiner XML-Leser (ohne DTD, ohne Entitäten-Erweiterung)
// ═════════════════════════════════════════════════════════════════════════════

export interface XmlNode {
  /** Name ohne Namensraum-Präfix */
  name: string;
  attrs: Record<string, string>;
  children: XmlNode[];
  text: string;
}

const MAX_XML = 25 * 1024 * 1024;

function decodeEntities(s: string): string {
  if (!s.includes("&")) return s;
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|lt|gt|amp|quot|apos);/g, (all, e: string) => {
    if (e === "lt") return "<";
    if (e === "gt") return ">";
    if (e === "amp") return "&";
    if (e === "quot") return '"';
    if (e === "apos") return "'";
    const code = e.startsWith("#x") ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : all;
  });
}

const localName = (n: string) => n.slice(n.indexOf(":") + 1);

/** Liest XML in einen Baum (Elemente, Attribute, Text). DTDs werden übersprungen, Entitäten nicht erweitert. */
export function parseXml(input: string): XmlNode {
  if (input.length > MAX_XML) throw new StatementError("Die Datei ist zu groß (mehr als 25 MB).");
  const src = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const root: XmlNode = { name: "#document", attrs: {}, children: [], text: "" };
  const stack: XmlNode[] = [root];
  let i = 0;
  const fail = (what: string): never => {
    throw new StatementError(`Die Datei ist kein gültiges XML (${what}, Zeichen ${i}).`);
  };
  while (i < src.length) {
    const lt = src.indexOf("<", i);
    const top = stack[stack.length - 1]!;
    if (lt < 0) {
      top.text += decodeEntities(src.slice(i));
      break;
    }
    if (lt > i) top.text += decodeEntities(src.slice(i, lt));
    i = lt;
    if (src.startsWith("<!--", i)) {
      const end = src.indexOf("-->", i + 4);
      if (end < 0) fail("Kommentar ohne Ende");
      i = end + 3;
    } else if (src.startsWith("<![CDATA[", i)) {
      const end = src.indexOf("]]>", i + 9);
      if (end < 0) fail("CDATA ohne Ende");
      top.text += src.slice(i + 9, end);
      i = end + 3;
    } else if (src.startsWith("<?", i)) {
      const end = src.indexOf("?>", i + 2);
      if (end < 0) fail("Verarbeitungsanweisung ohne Ende");
      i = end + 2;
    } else if (src.startsWith("<!", i)) {
      // DOCTYPE und Deklarationen überspringen (inklusive interner Teilmenge in [ ]), nie auswerten
      let depth = 0;
      let j = i + 2;
      for (; j < src.length; j++) {
        const c = src[j];
        if (c === "[") depth++;
        else if (c === "]") depth--;
        else if (c === ">" && depth <= 0) break;
      }
      if (j >= src.length) fail("Deklaration ohne Ende");
      i = j + 1;
    } else if (src.startsWith("</", i)) {
      const end = src.indexOf(">", i);
      if (end < 0) fail("schließendes Element ohne Ende");
      const name = localName(src.slice(i + 2, end).trim());
      const open = stack.pop();
      if (!open || open === root || open.name !== name) fail(`„${name}“ passt nicht zum geöffneten Element`);
      i = end + 1;
    } else {
      // Öffnendes Element: Name, Attribute, ggf. selbstschließend
      let j = i + 1;
      let quote: string | null = null;
      for (; j < src.length; j++) {
        const c = src[j];
        if (quote) {
          if (c === quote) quote = null;
        } else if (c === '"' || c === "'") quote = c;
        else if (c === ">") break;
      }
      if (j >= src.length) fail("Element ohne Ende");
      let body = src.slice(i + 1, j);
      const selfClosing = body.endsWith("/");
      if (selfClosing) body = body.slice(0, -1);
      const nm = /^([^\s/>]+)/.exec(body);
      if (!nm) fail("Element ohne Namen");
      const node: XmlNode = { name: localName(nm![1]!), attrs: {}, children: [], text: "" };
      const attrRe = /([^\s=]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
      for (let m = attrRe.exec(body.slice(nm![1]!.length)); m; m = attrRe.exec(body.slice(nm![1]!.length))) {
        node.attrs[localName(m[1]!)] = decodeEntities(m[3] ?? m[4] ?? "");
      }
      top.children.push(node);
      if (!selfClosing) stack.push(node);
      i = j + 1;
    }
  }
  if (stack.length > 1) throw new StatementError(`Die Datei ist kein gültiges XML („${stack[stack.length - 1]!.name}“ ist nicht geschlossen).`);
  return root;
}

/** Erstes Kind-Element entlang eines Pfads „A/B/C“. */
export function xmlPath(node: XmlNode | null | undefined, path: string): XmlNode | null {
  let cur: XmlNode | null | undefined = node;
  for (const part of path.split("/")) {
    if (!cur) return null;
    cur = cur.children.find((c) => c.name === part) ?? null;
  }
  return cur ?? null;
}
/** Alle Elemente am Ende eines Pfads „A/B/C“ (auch mehrfach vorkommende Zwischenelemente). */
export function xmlAll(node: XmlNode | null | undefined, path: string): XmlNode[] {
  if (!node) return [];
  let cur: XmlNode[] = [node];
  for (const part of path.split("/")) cur = cur.flatMap((n) => n.children.filter((c) => c.name === part));
  return cur;
}
/** Text am Pfad (getrimmt), sonst null. */
export function xmlText(node: XmlNode | null | undefined, path: string): string | null {
  const n = xmlPath(node, path);
  const t = n ? n.text.trim() : "";
  return t ? t : null;
}

// ═════════════════════════════════════════════════════════════════════════════
// CAMT.053 / .052 / .054
// ═════════════════════════════════════════════════════════════════════════════

const firstText = (node: XmlNode | null | undefined, paths: string[]): string | null => {
  for (const p of paths) {
    const t = xmlText(node, p);
    if (t) return t;
  }
  return null;
};

function dateOf(node: XmlNode | null | undefined): IsoDate | null {
  return parseStatementDate(firstText(node, ["Dt", "DtTm"]));
}

function amountOf(node: XmlNode | null): { cents: Cents; currency: string } | null {
  if (!node) return null;
  const cents = parseDecimalCents(node.text, ".");
  if (cents === null || cents < 0) return null;
  return { cents, currency: (node.attrs["Ccy"] ?? "").toUpperCase() };
}

function partyName(p: XmlNode | null): string | null {
  // camt v2–v7: Dbtr/Nm; ab v8: Dbtr/Pty/Nm
  return firstText(p, ["Nm", "Pty/Nm", "Agt/FinInstnId/Nm"]);
}
function accountId(a: XmlNode | null): string | null {
  return firstText(a, ["Id/IBAN", "Id/Othr/Id"]);
}

/** Verwendungszweck und Referenzen aus RmtInf (unstrukturiert und strukturiert). */
function remittanceOf(tx: XmlNode | null): { text: string[]; creditorRef: string | null; docs: string[] } {
  const r = xmlPath(tx, "RmtInf");
  const text = xmlAll(r, "Ustrd").map((u) => oneLine(u.text)).filter(Boolean);
  let creditorRef: string | null = null;
  const docs: string[] = [];
  for (const s of xmlAll(r, "Strd")) {
    creditorRef ??= xmlText(s, "CdtrRefInf/Ref");
    for (const d of xmlAll(s, "RfrdDocInf")) {
      const nb = xmlText(d, "Nb");
      if (nb) docs.push(oneLine(nb));
    }
    for (const a of xmlAll(s, "AddtlRmtInf")) if (a.text.trim()) text.push(oneLine(a.text));
  }
  return { text, creditorRef: creditorRef ? oneLine(creditorRef) : null, docs };
}

function bankCodeOf(n: XmlNode | null): string | null {
  const d = xmlText(n, "BkTxCd/Domn/Cd");
  if (d) return [d, xmlText(n, "BkTxCd/Domn/Fmly/Cd"), xmlText(n, "BkTxCd/Domn/Fmly/SubFmlyCd")].filter(Boolean).join("/");
  return xmlText(n, "BkTxCd/Prtry/Cd");
}

/** Liest einen CAMT-Kontoauszug (053 Tagesauszug, 052 Umsatzbericht, 054 Avis). Unbekannte Formate → StatementError. */
export function parseCamt(xml: string): BankStatement {
  const doc = parseXml(xml);
  const root = doc.children.find((c) => c.name === "Document") ?? doc.children[0];
  if (!root) throw new StatementError("Die Datei ist leer.");
  const kinds: [string, string, StatementFormat][] = [["BkToCstmrStmt", "Stmt", "camt053"], ["BkToCstmrAcctRpt", "Rpt", "camt052"], ["BkToCstmrDbtCdtNtfctn", "Ntfctn", "camt054"]];
  const kind = kinds.find(([k]) => root.name === k || root.children.some((c) => c.name === k));
  if (!kind) throw new StatementError("Das ist kein Kontoauszug im Format CAMT.053, CAMT.052 oder CAMT.054 (ISO 20022).");
  const [wrapper, part, format] = kind;
  const body = root.name === wrapper ? root : xmlPath(root, wrapper)!;
  const reports = xmlAll(body, part);
  const out: BankStatement = { format, accountIban: null, accountCurrency: null, statementId: null, from: null, to: null, closingBalance: null, transactions: [], warnings: [] };
  const accounts = new Set<string>();
  for (const st of reports) {
    const acct = xmlPath(st, "Acct");
    const iban = firstText(acct, ["Id/IBAN", "Id/Othr/Id"]);
    if (iban) accounts.add(iban.replace(/\s+/g, "").toUpperCase());
    out.accountIban ??= iban ? iban.replace(/\s+/g, "").toUpperCase() : null;
    out.accountCurrency ??= xmlText(acct, "Ccy");
    out.statementId ??= xmlText(st, "Id");
    const from = parseStatementDate(firstText(st, ["FrToDt/FrDtTm", "FrToDt/FrDt"]));
    const to = parseStatementDate(firstText(st, ["FrToDt/ToDtTm", "FrToDt/ToDt"]));
    if (from && (!out.from || from < out.from)) out.from = from;
    if (to && (!out.to || to > out.to)) out.to = to;
    for (const bal of xmlAll(st, "Bal")) {
      if (xmlText(bal, "Tp/CdOrPrtry/Cd") !== "CLBD") continue;
      const a = amountOf(xmlPath(bal, "Amt"));
      if (!a) continue;
      out.closingBalance = { amountCents: xmlText(bal, "CdtDbtInd") === "DBIT" ? -a.cents : a.cents, currency: a.currency, date: dateOf(xmlPath(bal, "Dt")) };
    }
    let n = 0;
    for (const e of xmlAll(st, "Ntry")) {
      n++;
      readEntry(e, n, out);
    }
  }
  if (accounts.size > 1) out.warnings.push(`Die Datei enthält ${accounts.size} Konten; alle Buchungen werden gelesen.`);
  if (!reports.length) out.warnings.push("Die Datei enthält keinen Auszug.");
  return out;
}

function readEntry(e: XmlNode, index: number, out: BankStatement): void {
  const entryAmount = amountOf(xmlPath(e, "Amt"));
  const dir = xmlText(e, "CdtDbtInd");
  const status = firstText(e, ["Sts/Cd", "Sts"]);
  const pending = status === "PDNG" || status === "INFO";
  const booking = dateOf(xmlPath(e, "BookgDt")) ?? dateOf(xmlPath(e, "ValDt"));
  const value = dateOf(xmlPath(e, "ValDt"));
  const entryRef = xmlText(e, "AcctSvcrRef");
  const reversal = xmlText(e, "RvslInd") === "true";
  const addtl = oneLine(xmlText(e, "AddtlNtryInf"));
  const code = bankCodeOf(e);
  if (!entryAmount || (dir !== "CRDT" && dir !== "DBIT") || !booking) {
    out.warnings.push(`Buchung ${index} übersprungen: Betrag, Soll/Haben oder Buchungsdatum fehlt.`);
    return;
  }
  const details = xmlAll(e, "NtryDtls/TxDtls");
  // Sammelbuchung: mehrere Einzelposten mit eigenem Betrag werden einzeln abgeglichen
  const withAmounts = details.map((t) => amountOf(xmlPath(t, "Amt")) ?? amountOf(xmlPath(t, "AmtDtls/TxAmt/Amt")) ?? amountOf(xmlPath(t, "AmtDtls/InstdAmt/Amt")));
  const split = details.length > 1 && withAmounts.every((a) => a && a.currency === entryAmount.currency);
  const parts: { tx: XmlNode | null; amount: { cents: Cents; currency: string }; ref: string | null }[] = split
    ? details.map((t, k) => ({ tx: t, amount: withAmounts[k]!, ref: xmlText(t, "Refs/AcctSvcrRef") ?? (entryRef ? `${entryRef}#${k + 1}` : null) }))
    : [{ tx: details[0] ?? null, amount: entryAmount, ref: (details.length === 1 ? xmlText(details[0], "Refs/AcctSvcrRef") : null) ?? entryRef }];
  for (const p of parts) {
    const txDir = xmlText(p.tx, "CdtDbtInd") ?? dir;
    const credit = txDir === "CRDT";
    // Gegenpartei: bei Eingängen der Zahler (Dbtr, sonst UltmtDbtr), bei Abgängen der Empfänger
    const party = credit ? xmlPath(p.tx, "RltdPties/Dbtr") ?? xmlPath(p.tx, "RltdPties/UltmtDbtr") : xmlPath(p.tx, "RltdPties/Cdtr") ?? xmlPath(p.tx, "RltdPties/UltmtCdtr");
    const acct = credit ? xmlPath(p.tx, "RltdPties/DbtrAcct") : xmlPath(p.tx, "RltdPties/CdtrAcct");
    const rem = remittanceOf(p.tx);
    // Nur wenn es keine Einzelposten gibt, Texte aller Posten und der Buchung zusammenfügen
    const extra = split ? [] : details.slice(1).flatMap((t) => remittanceOf(t).text);
    const texts = [...rem.text, ...extra, oneLine(xmlText(p.tx, "AddtlTxInf"))].filter(Boolean);
    if (!texts.length && addtl) texts.push(addtl);
    const ret = xmlText(p.tx, "RtrInf/Rsn/Cd") ?? xmlText(p.tx, "RtrInf/Rsn/Prtry");
    const e2e = xmlText(p.tx, "Refs/EndToEndId");
    out.transactions.push({
      bankRef: p.ref,
      endToEndId: e2e && e2e !== "NOTPROVIDED" ? e2e : null,
      bookingDate: booking,
      valueDate: value,
      direction: credit ? "credit" : "debit",
      amountCents: p.amount.cents,
      currency: p.amount.currency || entryAmount.currency,
      counterpartyName: partyName(party) ? oneLine(partyName(party)) : null,
      counterpartyIban: accountId(acct)?.replace(/\s+/g, "").toUpperCase() ?? null,
      remittance: texts.join(" ").slice(0, 1000),
      creditorRef: rem.creditorRef,
      referredDocs: rem.docs,
      reversal: reversal || !!ret,
      returnReason: ret,
      bankCode: bankCodeOf(p.tx) ?? code,
      pending,
    });
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// CSV mit Spaltenzuordnung
// ═════════════════════════════════════════════════════════════════════════════

export interface CsvTable {
  delimiter: string;
  /** Zeile (0-basiert) mit den Spaltenüberschriften */
  headerRow: number;
  headers: string[];
  rows: string[][];
  /** Zeilennummer (1-basiert) jeder Datenzeile, für Hinweise */
  lines: number[];
}

/** Zerlegt CSV-Text (Anführungszeichen nach RFC 4180) in Zeilen und Zellen. */
export function splitCsv(text: string, delimiter: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; }
        else quoted = false;
      } else cell += c;
    } else if (c === '"' && cell.trim() === "") {
      quoted = true;
      cell = "";
    } else if (c === delimiter) {
      row.push(cell);
      cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += c;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.map((r) => r.map((x) => x.trim()));
}

const isEmptyRow = (r: readonly string[]) => r.every((x) => x === "");

const HEADER_WORDS = ["datum", "buchung", "valuta", "betrag", "umsatz", "verwendungszweck", "zweck", "iban", "empfanger", "auftraggeber",
  "zahlungspflichtiger", "name", "wahrung", "waehrung", "soll", "haben", "gutschrift", "lastschrift", "date", "amount", "payee", "payer",
  "reference", "description", "currency", "text", "avisierungstext", "buchungstext", "beguenstigter"];

function headerScore(cells: readonly string[]): number {
  return cells.filter((c) => {
    const f = foldText(c);
    return HEADER_WORDS.some((w) => f.includes(w));
  }).length;
}

/** Liest eine CSV-Datei: Trennzeichen (; , Tab |) und Kopfzeile erkennt sie selbst, Vorspann-Zeilen der Bank fallen weg. */
export function readCsv(text: string, opts: { delimiter?: string } = {}): CsvTable {
  const sample = text.slice(0, 20000);
  const delimiter = opts.delimiter ?? [";", ",", "\t", "|"]
    .map((d) => ({ d, n: splitCsv(sample, d).slice(0, 30).reduce((s, r) => s + (r.length > 1 ? r.length : 0), 0) }))
    .sort((a, b) => b.n - a.n)[0]!.d;
  const all = splitCsv(text, delimiter);
  if (!all.some((r) => !isEmptyRow(r))) throw new StatementError("Die CSV-Datei ist leer.");
  let headerRow = all.findIndex((r) => !isEmptyRow(r));
  let best = 0;
  for (let i = 0; i < Math.min(all.length, 25); i++) {
    const s = headerScore(all[i]!);
    if (s > best && all[i]!.length >= 3) {
      best = s;
      headerRow = i;
    }
  }
  const headers = all[headerRow]!.map((h, i) => h || `Spalte ${i + 1}`);
  const rows: string[][] = [];
  const lines: number[] = [];
  all.forEach((r, i) => {
    if (i <= headerRow || isEmptyRow(r) || r.length < Math.min(3, headers.length)) return;
    rows.push(r);
    lines.push(i + 1);
  });
  return { delimiter, headerRow, headers, rows, lines };
}

export interface CsvMapping {
  bookingDate: number;
  valueDate?: number | null;
  /** Betrag mit Vorzeichen (Eingang positiv) … */
  amount?: number | null;
  /** … oder getrennte Spalten für Eingang (Haben) und Abgang (Soll) */
  credit?: number | null;
  debit?: number | null;
  /** Spalte mit Soll/Haben-Kennzeichen (S/H, Soll/Haben, D/C, Lastschrift/Gutschrift), falls der Betrag ohne Vorzeichen steht */
  direction?: number | null;
  currency?: number | null;
  name?: number | null;
  iban?: number | null;
  /** Verwendungszweck, auch über mehrere Spalten */
  purpose?: number[];
  reference?: number | null;
  decimal: "," | ".";
  /** Währung, wenn die Datei keine Spalte dafür hat */
  defaultCurrency: string;
}

export type CsvField = "bookingDate" | "valueDate" | "amount" | "credit" | "debit" | "direction" | "currency" | "name" | "iban" | "purpose" | "reference";

const GUESS: [CsvField, RegExp][] = [
  ["bookingDate", /^(buchungstag|buchungsdatum|buchung|datum|date|booking date|transaktionsdatum|abschlussdatum)$/],
  ["valueDate", /^(valuta|valutadatum|wertstellung|value date|valuta datum)$/],
  ["credit", /^(gutschrift|haben|eingang|credit|gutschrift in [a-z]{3}|haben in [a-z]{3})$/],
  ["debit", /^(lastschrift|soll|ausgang|debit|belastung|lastschrift in [a-z]{3}|soll in [a-z]{3})$/],
  ["amount", /^(betrag|umsatz|amount|betrag \([a-z]{3}\)|betrag in [a-z]{3}|betrag [a-z]{3})$/],
  ["direction", /^(soll\/haben|s\/h|soll haben|credit\/debit|kennzeichen)$/],
  ["currency", /^(wahrung|waehrung|currency|whrg)$/],
  ["name", /^(beguenstigter\/zahlungspflichtiger|begunstigter\/zahlungspflichtiger|auftraggeber\/empfanger|zahlungspflichtiger|auftraggeber|name|empfanger|zahlungsbeteiligter|gegenpartei|payer|payee|counterparty|name zahlungsbeteiligter|partnername|partner name|name des partners|name partner|counterparty name|name der gegenpartei|beneficiary|beneficiary name|sender|sender name|empfanger\/auftraggeber)$/],
  ["iban", /^(iban|kontonummer\/iban|iban zahlungsbeteiligter|gegenkonto|iban auftraggeber|konto|kontonummer|partner iban|partner-iban|iban des partners|partner kontonummer)$/],
  ["reference", /^(buchungs-id|referenz|bankreferenz|transaktions-id|transaction id|reference number|belegnummer)$/],
];

/** Schlägt eine Spaltenzuordnung vor (deutsche, österreichische und Schweizer Bankexporte, englische Überschriften). */
export function guessCsvMapping(table: CsvTable, defaultCurrency: string): CsvMapping {
  const folded = table.headers.map((h) => foldText(h).replace(/\s+/g, " "));
  const take = (f: CsvField) => folded.findIndex((h, i) => GUESS.find(([k]) => k === f)![1].test(h) && !used.has(i));
  const used = new Set<number>();
  const pick = (f: CsvField): number | null => {
    const i = take(f);
    if (i < 0) return null;
    used.add(i);
    return i;
  };
  const bookingDate = pick("bookingDate") ?? folded.findIndex((h) => h.includes("datum") || h.includes("date"));
  if (bookingDate >= 0) used.add(bookingDate);
  const valueDate = pick("valueDate");
  const credit = pick("credit");
  const debit = pick("debit");
  const amount = credit !== null ? null : pick("amount") ?? (() => {
    const i = folded.findIndex((h, k) => !used.has(k) && (h.includes("betrag") || h.includes("amount") || h.includes("umsatz")));
    if (i >= 0) used.add(i);
    return i >= 0 ? i : null;
  })();
  const direction = pick("direction");
  const currency = pick("currency");
  const name = pick("name");
  const iban = pick("iban");
  const reference = pick("reference");
  const purpose = folded.map((h, i) => ({ h, i }))
    .filter(({ h, i }) => !used.has(i) && (/verwendungszweck|zweck|purpose|description|mitteilung|avisierungstext|buchungstext|details|text|remittance|payment reference|zahlungsreferenz|^reference$|narrative/.test(h)))
    .map(({ i }) => i);
  // Dezimaltrenner an den Beträgen erkennen
  const amountCols = [amount, credit, debit].filter((x): x is number => x !== null && x >= 0);
  let commas = 0, points = 0;
  for (const r of table.rows.slice(0, 50)) {
    for (const c of amountCols) {
      const v = r[c] ?? "";
      if (/,\d{1,2}-?$/.test(v)) commas++;
      if (/\.\d{1,2}-?$/.test(v)) points++;
    }
  }
  return { bookingDate: Math.max(bookingDate, 0), valueDate, amount, credit, debit, direction, currency, name, iban, purpose, reference, decimal: points > commas ? "." : ",", defaultCurrency };
}

function directionWord(v: string): "credit" | "debit" | null {
  const f = foldText(v);
  if (/^(h|haben|c|cr|crdt|credit|gutschrift|eingang|\+)$/.test(f)) return "credit";
  if (/^(s|soll|d|dr|dbit|debit|lastschrift|belastung|ausgang|-)$/.test(f)) return "debit";
  return null;
}

/** Liest die CSV-Zeilen mit einer Spaltenzuordnung. Zeilen ohne Datum oder Betrag werden mit Hinweis übersprungen. */
export function parseCsvStatement(table: CsvTable, mapping: CsvMapping): BankStatement {
  const out: BankStatement = { format: "csv", accountIban: null, accountCurrency: mapping.defaultCurrency, statementId: null, from: null, to: null, closingBalance: null, transactions: [], warnings: [] };
  const cell = (r: string[], i: number | null | undefined) => (i === null || i === undefined || i < 0 ? "" : (r[i] ?? "").trim());
  table.rows.forEach((r, k) => {
    const line = table.lines[k] ?? table.headerRow + k + 2;
    const booking = parseStatementDate(cell(r, mapping.bookingDate));
    if (!booking) {
      out.warnings.push(`Zeile ${line} übersprungen: kein Buchungsdatum.`);
      return;
    }
    let cents: number | null = null;
    if (mapping.credit !== null && mapping.credit !== undefined || mapping.debit !== null && mapping.debit !== undefined) {
      const c = parseDecimalCents(cell(r, mapping.credit), mapping.decimal);
      const d = parseDecimalCents(cell(r, mapping.debit), mapping.decimal);
      if (c !== null && c !== 0) cents = Math.abs(c);
      else if (d !== null && d !== 0) cents = -Math.abs(d);
      else if (c === 0 || d === 0) cents = 0;
    } else {
      cents = parseDecimalCents(cell(r, mapping.amount), mapping.decimal);
      const dw = mapping.direction !== null && mapping.direction !== undefined ? directionWord(cell(r, mapping.direction)) : null;
      if (cents !== null && dw) cents = dw === "debit" ? -Math.abs(cents) : Math.abs(cents);
    }
    if (cents === null) {
      out.warnings.push(`Zeile ${line} übersprungen: Betrag nicht lesbar.`);
      return;
    }
    if (cents === 0) return;
    const purpose = (mapping.purpose ?? []).map((i) => cell(r, i)).filter(Boolean).join(" ");
    const iban = cell(r, mapping.iban).replace(/\s+/g, "").toUpperCase() || null;
    const cur = (cell(r, mapping.currency) || mapping.defaultCurrency).toUpperCase();
    // Strukturierte Referenz im Text (RF…, auch in Vierergruppen) übernehmen
    const rf = findRfReference(purpose);
    out.transactions.push({
      bankRef: cell(r, mapping.reference) || null,
      endToEndId: null,
      bookingDate: booking,
      valueDate: parseStatementDate(cell(r, mapping.valueDate)),
      direction: cents > 0 ? "credit" : "debit",
      amountCents: Math.abs(cents),
      currency: /^[A-Z]{3}$/.test(cur) ? cur : mapping.defaultCurrency,
      counterpartyName: oneLine(cell(r, mapping.name)) || null,
      counterpartyIban: iban,
      remittance: oneLine(purpose).slice(0, 1000),
      creditorRef: rf,
      referredDocs: [],
      reversal: /r[uü]ckbuchung|r[uü]cklastschrift|retoure|storno/i.test(`${purpose} ${cell(r, mapping.direction)}`),
      returnReason: null,
      bankCode: null,
      pending: false,
    });
    if (!out.from || booking < out.from) out.from = booking;
    if (!out.to || booking > out.to) out.to = booking;
  });
  return out;
}

/** Erkennt das Format am Inhalt: XML mit CAMT-Wurzel oder CSV. */
export function detectStatementFormat(text: string): "xml" | "csv" {
  const head = text.replace(/^﻿/, "").trimStart().slice(0, 200);
  return head.startsWith("<") ? "xml" : "csv";
}

// ═════════════════════════════════════════════════════════════════════════════
// Fingerabdruck gegen doppelte Importe
// ═════════════════════════════════════════════════════════════════════════════

const squash = (s: string | null | undefined) => foldText(s ?? "").replace(/[^0-9a-z]/g, "");

/**
 * Schlüssel einer Buchung: Referenz der Bank (AcctSvcrRef bzw. Buchungs-ID), sonst Datum, Richtung, Betrag, Währung,
 * Verwendungszweck und die maskierte Gegen-IBAN (nie die volle: Datum, Betrag und Zweck stehen in der Datenbank, aus einem
 * Fingerabdruck über die volle IBAN ließe sie sich sonst zurückrechnen). Unverändert nur in diesem Modul; gespeichert wird nur der SHA-256-Wert.
 */
export function transactionKey(tx: BankTransaction, ownAccount: string | null): string {
  const own = (ownAccount ?? "").replace(/\s+/g, "").toUpperCase();
  if (tx.bankRef) return ["R", own, tx.bankRef.trim(), tx.direction, tx.amountCents, tx.currency].join("|");
  return ["H", tx.bookingDate, tx.direction, tx.amountCents, tx.currency, squash(`${tx.remittance} ${tx.creditorRef ?? ""}`),
    maskIban(tx.counterpartyIban) ?? ""].join("|");
}

/**
 * Fingerabdrücke je Buchung (SHA-256 aus Agentur und Schlüssel). Gleiche Buchungen innerhalb einer Datei (zweimal
 * dieselbe Teilzahlung am selben Tag) bekommen eine laufende Nummer, damit keine davon als Dublette verloren geht.
 */
export function fingerprintTransactions(txs: readonly BankTransaction[], orgId: string, ownAccount: string | null): string[] {
  const seen = new Map<string, number>();
  return txs.map((tx) => {
    const key = transactionKey(tx, ownAccount);
    const n = (seen.get(key) ?? 0) + 1;
    seen.set(key, n);
    return sha256Hex(`tx|${orgId.toLowerCase()}|${key}${n > 1 ? `#${n}` : ""}`);
  });
}

/** Ist die Buchung eine Rücklastschrift bzw. Rückbuchung? (Zur Anzeige: prüfen, ob eine Zahlung zurückzusetzen ist.) */
export function isReturnTransaction(tx: Pick<BankTransaction, "reversal" | "returnReason" | "remittance" | "bankCode">): boolean {
  return tx.reversal || !!tx.returnReason || /r[uü]cklastschrift|r[uü]ckbuchung|retoure|return|chargeback/i.test(tx.remittance)
    || /(RRTN|RTRN|ARET|RCDD|UPDD)$/.test(tx.bankCode ?? "");
}

/**
 * Haben-Buchung, die eine zurückgekommene eigene Zahlung ist (Rückläufer, z. B. eine Creator-Auszahlung mit falscher
 * IBAN): Storno-Kennzeichen, Rückgabegrund, Buchungscode oder eindeutige Worte. Anders als isReturnTransaction zählen
 * „Retoure“ oder „Return“ hier nicht, weil Händler so Erstattungen beschriften. Solche Eingänge sind keine Zahlungen
 * auf Rechnungen und werden nur angezeigt.
 */
export function isReturnedCredit(tx: Pick<BankTransaction, "direction" | "reversal" | "returnReason" | "remittance" | "bankCode">): boolean {
  return tx.direction === "credit" && (tx.reversal || !!tx.returnReason || /(RRTN|RTRN|ARET|RCDD|UPDD)$/.test(tx.bankCode ?? "")
    || /r[uü]ckl[aä]ufer|r[uü]ck[uü]berweisung|r[uü]ckbuchung|r[uü]cklastschrift|nicht zustellbar/i.test(tx.remittance));
}

// ═════════════════════════════════════════════════════════════════════════════
// CAMT.053 schreiben (Beispieldateien, Tests)
// ═════════════════════════════════════════════════════════════════════════════

export interface Camt053Entry {
  ref: string;
  bookingDate: IsoDate;
  valueDate?: IsoDate | null;
  direction: "credit" | "debit";
  amountCents: Cents;
  counterpartyName?: string | null;
  counterpartyIban?: string | null;
  remittance?: string | null;
  creditorRef?: string | null;
  reversal?: boolean;
  endToEndId?: string | null;
}
export interface Camt053Input {
  messageId: string;
  createdAt: string;
  accountIban: string;
  accountOwner: string;
  currency: string;
  statementId: string;
  from: IsoDate;
  to: IsoDate;
  openingBalanceCents: Cents;
  entries: readonly Camt053Entry[];
}

const xe = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const amt = (c: Cents) => (Math.abs(c) / 100).toFixed(2);

/** Erzeugt einen Tagesauszug camt.053.001.08 (für Beispielauszüge und Tests). */
export function buildCamt053(input: Camt053Input): string {
  const closing = input.entries.reduce((s, e) => s + (e.direction === "credit" ? e.amountCents : -e.amountCents), input.openingBalanceCents);
  const bal = (code: string, cents: Cents, day: IsoDate) =>
    `<Bal><Tp><CdOrPrtry><Cd>${code}</Cd></CdOrPrtry></Tp><Amt Ccy="${input.currency}">${amt(cents)}</Amt><CdtDbtInd>${cents < 0 ? "DBIT" : "CRDT"}</CdtDbtInd><Dt><Dt>${day}</Dt></Dt></Bal>`;
  const entries = input.entries.map((e) => {
    const credit = e.direction === "credit";
    const party = e.counterpartyName || e.counterpartyIban
      ? `<RltdPties>${credit
        ? `${e.counterpartyName ? `<Dbtr><Pty><Nm>${xe(e.counterpartyName)}</Nm></Pty></Dbtr>` : ""}${e.counterpartyIban ? `<DbtrAcct><Id><IBAN>${xe(e.counterpartyIban)}</IBAN></Id></DbtrAcct>` : ""}`
        : `${e.counterpartyName ? `<Cdtr><Pty><Nm>${xe(e.counterpartyName)}</Nm></Pty></Cdtr>` : ""}${e.counterpartyIban ? `<CdtrAcct><Id><IBAN>${xe(e.counterpartyIban)}</IBAN></Id></CdtrAcct>` : ""}`}</RltdPties>`
      : "";
    const rmt = e.remittance || e.creditorRef
      ? `<RmtInf>${e.remittance ? `<Ustrd>${xe(e.remittance.slice(0, 140))}</Ustrd>` : ""}${e.creditorRef ? `<Strd><CdtrRefInf><Tp><CdOrPrtry><Cd>SCOR</Cd></CdOrPrtry></Tp><Ref>${xe(e.creditorRef)}</Ref></CdtrRefInf></Strd>` : ""}</RmtInf>`
      : "";
    return `<Ntry><NtryRef>${xe(e.ref)}</NtryRef><Amt Ccy="${input.currency}">${amt(e.amountCents)}</Amt><CdtDbtInd>${credit ? "CRDT" : "DBIT"}</CdtDbtInd>${e.reversal ? "<RvslInd>true</RvslInd>" : ""}`
      + `<Sts><Cd>BOOK</Cd></Sts><BookgDt><Dt>${e.bookingDate}</Dt></BookgDt><ValDt><Dt>${e.valueDate ?? e.bookingDate}</Dt></ValDt><AcctSvcrRef>${xe(e.ref)}</AcctSvcrRef>`
      + `<BkTxCd><Domn><Cd>PMNT</Cd><Fmly><Cd>${credit ? "RCDT" : "ICDT"}</Cd><SubFmlyCd>${e.reversal ? "RRTN" : credit ? "ESCT" : "ESCT"}</SubFmlyCd></Fmly></Domn></BkTxCd>`
      + `<NtryDtls><TxDtls><Refs><AcctSvcrRef>${xe(e.ref)}</AcctSvcrRef><EndToEndId>${xe(e.endToEndId ?? "NOTPROVIDED")}</EndToEndId></Refs><Amt Ccy="${input.currency}">${amt(e.amountCents)}</Amt>${party}${rmt}</TxDtls></NtryDtls></Ntry>`;
  }).join("\n      ");
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:camt.053.001.08">
  <BkToCstmrStmt>
    <GrpHdr><MsgId>${xe(input.messageId)}</MsgId><CreDtTm>${xe(input.createdAt)}</CreDtTm></GrpHdr>
    <Stmt>
      <Id>${xe(input.statementId)}</Id>
      <CreDtTm>${xe(input.createdAt)}</CreDtTm>
      <FrToDt><FrDtTm>${input.from}T00:00:00</FrDtTm><ToDtTm>${input.to}T23:59:59</ToDtTm></FrToDt>
      <Acct><Id><IBAN>${xe(input.accountIban)}</IBAN></Id><Ccy>${input.currency}</Ccy><Ownr><Nm>${xe(input.accountOwner)}</Nm></Ownr></Acct>
      ${bal("OPBD", input.openingBalanceCents, input.from)}
      ${bal("CLBD", closing, input.to)}
      ${entries}
    </Stmt>
  </BkToCstmrStmt>
</Document>
`;
}
